import {
  Accordion as MantineAccordion,
  AccordionControlProps as MantineAccordionControlProps,
  AccordionProps as MantineAccordionProps,
} from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';
import { ReactNode, forwardRef } from 'react';

import styles from './Accordion.module.css';

export type AccordionStyleVariant =
  'background' | 'border' | 'shadow' | 'underlined';
export type AccordionSize = 'sm' | 'md';
export type AccordionPanelOffset = 'default' | 'none';

export type AccordionProps<T extends boolean = false> = Omit<
  MantineAccordionProps<T>,
  'variant' | 'color' | 'styles' | 'classNames' | 'style' | 'radius'
> & {
  /** Visual style variant: "border" (default) or "shadow" */
  styleVariant?: AccordionStyleVariant;
  /** Size of accordion header: "sm" (56px) or "md" (64px, default) */
  size?: AccordionSize;
  /** "underlined" only: keep the divider on an open item, below its panel */
  withDividerOnOpen?: boolean;
  /** "underlined" only: divider under the last item, open or closed (default true) */
  withTrailingDivider?: boolean;
  /** "underlined" and "shadow": space between the control and the panel content */
  panelOffset?: AccordionPanelOffset;
};

export type AccordionControlProps = MantineAccordionControlProps & {
  /** Content rendered between the label and the chevron (e.g. action icons) */
  rightSection?: ReactNode;
  isNonExpandable?: boolean;
};

const classNames = {
  root: styles.root,
  item: styles.item,
  control: styles.control,
  label: styles.label,
  icon: styles.icon,
  chevron: styles.chevron,
  panel: styles.panel,
  content: styles.content,
};

const AccordionControl = forwardRef<HTMLButtonElement, AccordionControlProps>(
  ({ rightSection, isNonExpandable, children, ...props }, ref) => {
    return (
      <MantineAccordion.Control
        ref={ref}
        {...(isNonExpandable && {
          chevron: <span />,
          disabled: true,
          'data-non-expandable': true,
        })}
        {...props}
      >
        <span className={styles.labelText}>{children}</span>
        {rightSection && (
          <span className={styles.rightSection}>{rightSection}</span>
        )}
      </MantineAccordion.Control>
    );
  }
);

AccordionControl.displayName = 'AccordionControl';

export function Accordion<T extends boolean = false>({
  children,
  styleVariant = 'border',
  size = 'md',
  withDividerOnOpen,
  withTrailingDivider = true,
  panelOffset = 'default',
  chevron,
  chevronPosition = 'right',
  ...props
}: AccordionProps<T>) {
  return (
    <MantineAccordion
      unstyled
      classNames={classNames}
      chevron={chevron ?? <IconChevronDown size={16} stroke={2} />}
      chevronPosition={chevronPosition}
      data-style={styleVariant}
      data-accordion-size={size}
      data-divider-on-open={withDividerOnOpen || undefined}
      data-no-trailing-divider={withTrailingDivider ? undefined : true}
      data-panel-offset={panelOffset}
      data-chevron-position={chevronPosition}
      {...(props as MantineAccordionProps<boolean>)}
    >
      {children}
    </MantineAccordion>
  );
}

Accordion.displayName = 'Accordion';
Accordion.Item = MantineAccordion.Item;
Accordion.Control = AccordionControl;
Accordion.Panel = MantineAccordion.Panel;
